import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

import {useGrantResources} from 'src/components/grant-resources/useGrantResources';
import {FormInput, NumberInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ActionWithReasonButton} from 'src/components/save-with-reason/action-with-reason-button';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {CustomValidatedControl, ValidatedControl} from 'src/contexts/validation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {EntitlementGrantChangeLog} from 'src/modules/manage-entitlements/edit-grant/change-log';
import {GrantStatus} from 'src/openapi/admin/models/GrantStatus';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {FormSection} from '@pb/kit-component-form-section';
import {Integer} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import {CustomGroupItem} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';
import Loading from '@pb/kit-component-loading';
import {Textarea} from '@pb/kit-component-textarea-v2';
import {LocaleContext} from '@pb/kit-context-locale';
import {useShown} from '@pb/kit-hooks-use-shown';

import {ManageEntitlementsPage} from '../components/manage-entitlements-page';
import {GrantResources} from '../grant-resources/grant-resources';
import {useAssignResourcesToGrant} from '../grant-resources/useAssignResourcesToGrant';
import {useDischargeResourcesFromGrant} from '../grant-resources/useDischargeResourcesFromGrant';
import {getEntitlementsReturnPath} from '../utils';

import {
    GRANT_DESCRIPTION_CONTROL,
    GRANT_NAME_CONTROL,
} from './constants';
import {REMOVE_GRANT_MODAL_WIDTH, RemoveGrantModal} from './remove-grant-modal';
import {useUpdateGrant} from './useUpdateGrant';

import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {CheckBoxChangeData} from 'src/typedef';


const EditGrant = () => {
    const queryParams = useParams<{grantId: string}>();
    const grantId = queryParams.grantId || '';

    const {shown: deleteShown, onClick: openDelete, onClose: closeDelete} = useShown();
    const {hasAccess} = useAdminAccessRight();

    const navigate = useNavigate();
    const {state: navigationState} = useLocation();
    const {editGrantOpen, editGrantSubmit} = useEventTracking();
    const {locale} = useContext(LocaleContext);
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);

    const [busy, setBusy] = useState(false);

    const {
        isLoading: isGrantLoading,
        loadingError: grantLoadingError,
        data,
        setData,
        dataInitial,
        isChanged: isGrantChanged,
        saveChanges: updateGrant,
        discardChanges: discardGrantChanges,
    } = useUpdateGrant({grantId});

    const {
        isLoading: isResourcesLoading,
        loadingError: resourcesLoadingError,
        data: resources,
        loadData: loadResources,
    } = useGrantResources({grantId, pageNumber: 1, pageSize: 250});

    const {
        data: assignedResources,
        setData: setAssignedResources,
        isChanged: isAssignedResourcesChanged,
        saveChanges: assignResources,
        discardChanges: discardAssignedResourcesChanges,
    } = useAssignResourcesToGrant();

    const {
        data: dischargedResources,
        setData: setDischargedResources,
        isChanged: isDischargedResourcesChanged,
        saveChanges: dischargeResources,
        discardChanges: discardDischargedResourcesChanges,
    } = useDischargeResourcesFromGrant();

    const {
        proceed,
        setIsChunkChanged,
        isCurrentChunkChanged,
    } = useConfirmationChunk({chunkName: 'update-grant', onProceed: () => {
        setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        setTimeout(() => {
            loadResources();
        }, 1500);
    }});

    const resourceIds = useMemo(() => [...new Set([
        ...resources.resources
            .map(({externalId}) => externalId)
            .filter(externalId => !dischargedResources.includes(externalId)),
        ...assignedResources,
    ])], [resources, dischargedResources, assignedResources]);

    useEffect(() => {
        editGrantOpen();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setIsChunkChanged(isGrantChanged || isAssignedResourcesChanged || isDischargedResourcesChanged);
    }, [isGrantChanged, isAssignedResourcesChanged, isDischargedResourcesChanged, setIsChunkChanged]);


    const changeName = useCallback((name: string) => {
        setData(prev => ({...prev, name}));
    }, [setData]);

    const changeDescription = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, description: event.target.value}));
    }, [setData]);

    const blurDescription = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, description: event.currentTarget.value.trim()}));
    }, [setData]);

    const setManageableGrant = useCallback((changeData: CheckBoxChangeData) => {
        setData(prev => ({...prev, requiredApprovals: changeData.event.target.checked}));
    }, [setData]);

    const backToAllEntitlements = useCallback(() => {
        navigate(-1);
        editGrantSubmit({submit: false, success: true});
    }, [navigate, editGrantSubmit]);

    const saveChanges = useCallback(async (options?: ActionOptions) => {
        const calls = [];

        setBusy(true);
        if (isGrantChanged) {
            calls.push(await updateGrant());
        }
        if (isAssignedResourcesChanged) {
            calls.push(await assignResources({grantId, ...options}));
        }
        if (isDischargedResourcesChanged) {
            calls.push(await dischargeResources({grantId, ...options}));
        }
        setBusy(false);

        const errors = [...new Set(calls.filter(Boolean))];
        if (errors.length) {
            setErrorMessage(errors.join('\n'));
        } else {
            proceed();
        }
        editGrantSubmit({submit: true, success: !errors.length});
        return errors.length ? errors.join('\n') : undefined;
    }, [
        isGrantChanged,
        isAssignedResourcesChanged,
        isDischargedResourcesChanged,
        updateGrant,
        assignResources,
        dischargeResources,
        editGrantSubmit,
        grantId,
        proceed,
        setErrorMessage,
    ]);

    const discardChanges = useCallback(() => {
        discardGrantChanges();
        discardAssignedResourcesChanges();
        discardDischargedResourcesChanges();

        editGrantSubmit({submit: false, success: true});
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [
        editGrantSubmit, setSuccessMessage,
        discardGrantChanges, discardAssignedResourcesChanges,
        discardDischargedResourcesChanges,
    ]);

    const addResources = useCallback((resourceId: string) => {
        setDischargedResources(prev => prev.filter(id => id !== resourceId));
        if (!resourceIds.includes(resourceId)) {
            setAssignedResources(prev => [...new Set([...prev, resourceId])]);
        }
    }, [resourceIds, setAssignedResources, setDischargedResources]);

    const removeResources = useCallback((resourceId: string) => {
        setAssignedResources(prev => prev.filter(id => id !== resourceId));
        if (resourceIds.includes(resourceId)) {
            setDischargedResources(prev => [...new Set([...prev, resourceId])]);
        }
    }, [resourceIds, setDischargedResources, setAssignedResources]);

    return (
        <Skeleton shown={isGrantLoading || isResourcesLoading} view={<Loading fast={true}/>}>
            <ServerResponseHandler
                serverError={grantLoadingError || resourcesLoadingError}
                accessRight={ACCESS_RIGHTS.MANAGE_ENTITLEMENTS as AccessRight}
                sectionName="Edit Grant">
                <ManageEntitlementsPage
                    title={dataInitial.name}
                    buttonsBlock={(
                        <Group>
                            {hasAccess(ACCESS_RIGHTS.DELETE_ENTITLEMENTS) ? (
                                <Button variant="Type.SECONDARY" colorVariant="ERROR" onClick={openDelete}>
                                    Delete
                                </Button>
                            ) : null}
                            <Button variant="Type.TERTIARY" onClick={backToAllEntitlements}>
                                Cancel
                            </Button>
                            <Button
                                variant="Type.SECONDARY"
                                disabled={!isCurrentChunkChanged}
                                onClick={discardChanges}>
                                Discard Changes
                            </Button>
                            <ActionWithReasonButton action="Save" legacyLabel="Save Changes" busy={busy} disabled={!isCurrentChunkChanged} onConfirm={saveChanges}/>
                        </Group>
                    )}>
                    <Item className="group-item pd-l">
                        <FormSection headline="Grant Information">
                            <Group direction="v">
                                <CustomValidatedControl name={GRANT_NAME_CONTROL}>
                                    {error => (
                                        <FormInput
                                            value={data.name}
                                            width={300}
                                            maxLength={64}
                                            label={<RequiredMark label="Grant Name"/>}
                                            placeholder="Grant Name"
                                            onBlur={changeName}
                                            valid={error ? false : undefined}
                                            errorDescription={error}
                                        />
                                    )}
                                </CustomValidatedControl>
                                <FormInput
                                    value={data.id}
                                    readOnly={true}
                                    width={300}
                                    maxLength={64}
                                    label={<RequiredMark label="Grant External ID"/>}
                                    placeholder="Grant External ID"
                                />
                                <ValidatedControl name={GRANT_DESCRIPTION_CONTROL}>
                                    {invalid => (
                                        <WidthItem width={610}>
                                            <Label><RequiredMark label="Description"/></Label>
                                            <Textarea
                                                maxLength={255}
                                                color={invalid ? 'ERROR' : 'DEFAULT'}
                                                value={data.description}
                                                onBlur={blurDescription}
                                                onChange={changeDescription}
                                            />
                                        </WidthItem>
                                    )}
                                </ValidatedControl>
                                <div title={dataInitial.requiredApprovals
                                    ? 'Manageable Grant cannot be disabled once enabled'
                                    : undefined}>
                                    <CheckBox
                                        label="Manageable Grant"
                                        checked={data.requiredApprovals}
                                        disabled={dataInitial.requiredApprovals}
                                        onCheckBoxChange={
                                            !dataInitial.requiredApprovals
                                                ? setManageableGrant
                                                : undefined
                                        }
                                    />
                                </div>
                            </Group>
                        </FormSection>
                        <FormSection headline="Permission">
                            <Group gap="l">
                                <div style={{width: 290}}>
                                    <Label
                                        id="grant-status-select-label"
                                        htmlFor="grant-status-select">
                                        Status
                                    </Label>
                                    <ComboBox
                                        id="grant-status-select"
                                        disabled={true}
                                        aria-labelledby="grant-status-select-label"
                                        placeholder="Select Status"
                                        value={data.status}>
                                        <ComboboxItem value={GrantStatus.ALLOW} caption="Allow"/>
                                        <ComboboxItem value={GrantStatus.DENY} caption="Deny"/>
                                    </ComboBox>
                                </div>
                                <div style={{width: 300}}>
                                    <Label>Restrictions</Label>
                                    <CustomGroupItem label="Days to Delay">
                                        <NumberInput
                                            value={data.daysToDelay}
                                            readOnly={true}
                                            displayedValue={data.daysToDelay !== undefined
                                                ? Integer.format(data.daysToDelay, locale)
                                                : 'Enter Day(s)'}
                                            onBlur={() => undefined}
                                            width={180}
                                            maxValue={9999}
                                        />
                                    </CustomGroupItem>
                                </div>
                            </Group>
                        </FormSection>
                        <GrantResources
                            resourceIds={resourceIds}
                            onAddResource={addResources}
                            onRemoveResource={removeResources}
                        />
                    </Item>
                    <EntitlementGrantChangeLog grantId={grantId}/>
                    <Dialog
                        shown={deleteShown}
                        setShown={closeDelete}
                        wrapperProps={{style: {width: REMOVE_GRANT_MODAL_WIDTH}}}>
                        <RemoveGrantModal
                            grantId={grantId}
                            grantName={dataInitial.name}
                            onClose={closeDelete}
                            onDone={() => {
                                closeDelete();
                                setSuccessMessage('Grant removed successfully');
                                navigate(getEntitlementsReturnPath(navigationState));
                            }}
                        />
                    </Dialog>
                </ManageEntitlementsPage>
            </ServerResponseHandler>
        </Skeleton>
    );
};

export const ValidateEditGrant = () => (
    <ValidationContextProvider>
        <EditGrant/>
    </ValidationContextProvider>
);
